import { supabaseClient, useUser } from '@auth-v2';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { ModalBase, ModalHeader, ModalBody, ModalFooter, Button } from '@ui';
import { useTranslate } from '@i18n';

import { Input } from '../Input';
import { PasswordStrength } from '../PasswordStrength/PasswordStrength';

import * as css from './PasswordModal.css';

const formSchema = z
    .object({
        currentPassword: z.string().min(1, 'user_settings.password_required'),
        newPassword: z
            .string()
            .min(8, 'user_settings.new_password_minimum')
            .regex(/[A-Z]/, 'user_settings.uppercase_required')
            .regex(/[0-9]/, 'user_settings.number_required'),
        confirmPassword: z.string().min(1, 'user_settings.confirm_password_required'),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
        message: 'user_settings.passwords_do_not_match',
        path: ['confirmPassword'],
    });

type FormValues = z.infer<typeof formSchema>;

type PasswordModalProps = {
    modalOpen: boolean;
    setModalOpen: (open: boolean) => void;
};

export function PasswordModal({ modalOpen, setModalOpen }: PasswordModalProps) {
    const t = useTranslate();
    const {
        register,
        handleSubmit,
        watch,
        setError,
        formState: { errors, isValid, isSubmitting },
    } = useForm<FormValues>({
        mode: 'onChange',
        defaultValues: { currentPassword: '', newPassword: '', confirmPassword: '' },
        resolver: zodResolver(formSchema),
    });

    const isFieldError = (error: unknown): error is { field: keyof FormValues; message: string } => {
        return typeof error === 'object' && error !== null && 'field' in error && 'message' in error;
    };

    const { mutateAsync: changePassword, isPending } = useMutation({
        mutationFn: async ({ currentPassword, newPassword }: FormValues) => {
            const {
                data: { user },
            } = await supabaseClient.auth.getUser();

            const { error: signInError } = await supabaseClient.auth.signInWithPassword({
                email: user!.email!,
                password: currentPassword,
            });

            if (signInError) throw { field: 'currentPassword', message: 'user_settings.incorrect_password' };

            const { error: updateError } = await supabaseClient.auth.updateUser({ password: newPassword });

            if (updateError) throw { field: 'newPassword', message: 'user_settings.unexpected_password_error' };
        },

        onSuccess: () => setModalOpen(false),

        onError: (error: unknown) => {
            if (isFieldError(error)) {
                setError(error.field, { message: error.message });
            } else {
                setError('root', { message: 'user_settings.unexpected_password_error' });
            }
        },
    });

    return (
        <ModalBase open={modalOpen} onDismiss={() => setModalOpen(false)} variant="partial">
            <ModalHeader>{t('user_settings.change_password')}</ModalHeader>
            <ModalBody>
                <p>{t('user_settings.password_description')}</p>
                <form
                    id="change-password-form"
                    onSubmit={handleSubmit((data) => changePassword(data))}
                    className={css.form}
                >
                    <Input
                        type="password"
                        label={t('user_settings.current_password')}
                        {...register('currentPassword', { required: 'user_settings.password_required' })}
                    />
                    {errors.currentPassword && (
                        <p className={css.errorText}>{t(errors.currentPassword.message ?? '')}</p>
                    )}

                    <Input
                        type="password"
                        label={t('user_settings.new_password')}
                        {...register('newPassword', { required: 'user_settings.new_password_minimum' })}
                    />
                    <PasswordStrength password={watch('newPassword')} />
                    {errors.newPassword && <p className={css.errorText}>{t(errors.newPassword.message ?? '')}</p>}

                    <Input
                        type="password"
                        label={t('user_settings.confirm_new_password')}
                        {...register('confirmPassword', {
                            required: 'user_settings.confirm_new_password_required',
                        })}
                    />
                    {errors.confirmPassword && (
                        <p className={css.errorText}>{t(errors.confirmPassword.message ?? '')}</p>
                    )}
                </form>
                {errors.root && <p className={css.errorText}>{t(errors.root.message ?? '')}</p>}
            </ModalBody>
            <ModalFooter
                secondary={
                    <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>
                        {t('generic_ui.cancel')}
                    </Button>
                }
                primary={
                    <Button
                        variant="primary"
                        type="submit"
                        form="change-password-form"
                        isDisabled={!isValid || isSubmitting || isPending}
                    >
                        {t('generic_ui.save')}
                    </Button>
                }
            />
        </ModalBase>
    );
}
